/**
 * Lead Command Center demo API client (Supabase-native demo Worker).
 *
 * These endpoints only exist on the dedicated LCC demo Worker
 * (wrangler.lead-demo.toml → lead-command-center-demo-api). They are used by
 * the /demo/lcc/* views, which are only surfaced in
 * VITE_DEMO_MODE=lead-command-center.
 */

import { request } from './api';

export interface LccSummary {
  leadsByStatus: Record<string, number>;
  leadsBySource: Record<string, number>;
  totals: {
    leads: number;
    openFollowUps: number;
    overdueFollowUps: number;
    pendingApprovals: number;
    verifications: number;
    verifiedLeads: number;
    needsReview: number;
    newLeads: number;
  };
  verificationsByResult: Record<string, number>;
  recentEvents: LccLeadEvent[];
}

export interface LccLeadEvent {
  id: string;
  leadId: string;
  organizationId?: string;
  eventType: string;
  title: string;
  description?: string;
  actorType: string;
  actorName?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface LccFollowUp {
  id: string;
  leadId?: string;
  leadTitle?: string;
  organizationId?: string;
  title: string;
  description?: string;
  status: 'open' | 'done' | string;
  priority: string;
  dueAt?: string;
  assignedTo?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LccApproval {
  id: string;
  leadId?: string;
  leadTitle?: string;
  itemType: string;
  status: 'pending' | 'approved' | 'rejected' | string;
  title: string;
  description?: string;
  requestedByType: string;
  metadata: Record<string, unknown>;
  createdAt: string;
  resolvedAt?: string;
}

export interface LccVerification {
  id: string;
  leadId: string;
  leadTitle?: string;
  verificationType: string;
  result: string;
  score?: number;
  summary?: string;
  transcript?: string;
  metadata: Record<string, unknown>;
  createdAt: string;
}

export interface LccReports {
  total: number;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
  byCampaign: Record<string, number>;
  funnel: Array<{ status: string; label: string; count: number }>;
  outcomes: Array<{ status: string; label: string; count: number }>;
}

export interface LccLead {
  id: string;
  organizationId?: string;
  contactId?: string;
  title: string;
  contactName?: string;
  companyName?: string;
  email?: string;
  phone?: string;
  website?: string;
  message?: string;
  status: string;
  statusLabel: string;
  source: string;
  sourceDetail?: string;
  campaign?: string;
  priority: string;
  assignedTo?: string;
  aiSummary?: string;
  aiScore?: number;
  aiVerificationStatus: string;
  nextFollowUpAt?: string;
  lastActivityAt?: string;
  verified: boolean;
  verifiedAt?: string;
  verifiedMethod?: string;
  verifiedNotes?: string;
  verifiedBy?: string;
  nextStep?: string;
  nextStepUpdatedAt?: string;
  nextStepUpdatedBy?: string;
  createdAt: string;
  updatedAt: string;
  organization?: { id: string; name: string };
  contact?: { id: string; fullName: string; email?: string; phone?: string };
}

/** Body for PATCH /api/lcc/leads/:id — send only the fields being changed. */
export interface LccLeadPatch {
  title?: string;
  status?: string;
  priority?: string;
  assignedTo?: string | null;
  nextFollowUpAt?: string | null;
  contactName?: string | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  message?: string | null;
  source?: string;
  sourceDetail?: string | null;
  campaign?: string | null;
  organizationId?: string | null;
  contactId?: string | null;
}

export interface LccOrganization {
  id: string;
  name: string;
  slug: string;
  industry?: string;
  status: string;
  website?: string;
  phone?: string;
  email?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface LccOrganizationListItem extends LccOrganization {
  contactCount: number;
  openLeadCount: number;
  totalLeadCount: number;
  lastActivityAt: string;
}

export interface LccContact {
  id: string;
  organizationId?: string;
  firstName?: string;
  lastName?: string;
  fullName: string;
  email?: string;
  phone?: string;
  companyName?: string;
  roleTitle?: string;
  type: string;
  createdAt: string;
  updatedAt: string;
}

export interface LccContactListItem extends LccContact {
  organizationName?: string;
  openLeadCount: number;
  totalLeadCount: number;
  lastActivityAt: string;
}

export interface LccFormSubmission {
  id: string;
  organizationId?: string;
  leadId?: string;
  leadTitle?: string;
  formName: string;
  source: string;
  status: string;
  submittedName?: string;
  submittedEmail?: string;
  submittedPhone?: string;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface LccLeadDetail {
  lead: LccLead;
  organization?: LccOrganization;
  contact?: LccContact;
  followUps: LccFollowUp[];
  approvals: LccApproval[];
  verifications: LccVerification[];
  submission?: LccFormSubmission;
}

export interface LccOrganizationDetail {
  organization: LccOrganization;
  contacts: LccContact[];
  leads: LccLead[];
  recentEvents: LccLeadEvent[];
}

export interface LccContactDetail {
  contact: LccContact;
  organization?: LccOrganization;
  leads: LccLead[];
}

export type LccLoopFilter =
  | 'all'
  | 'due'
  | 'overdue'
  | 'approvals'
  | 'new'
  | 'unverified'
  | 'unassigned';

/** Editable content of an AI-proposed action (metadata.draft on an approval). */
export interface LccApprovalDraft {
  subject?: string;
  body?: string;
  /** Call notes / instructions for outgoing-call style actions. */
  instructions?: string;
}

export type LccLoopReason =
  | 'approval_pending'
  | 'overdue'
  | 'due_now'
  | 'new_lead'
  | 'needs_next_step';

/** One LeadLoop feed entry: a lead plus why it needs contact right now. */
export interface LccLoopItem {
  lead: LccLead;
  approval?: LccApproval;
  reason: LccLoopReason;
  dueAt?: string;
}

export interface LccDashboard {
  cards: {
    dueNow: number;
    overdue: number;
    pendingApprovals: number;
    unverifiedLeads: number;
    convertedThisMonth: number;
  };
  recentEvents: LccLeadEvent[];
}

// ---------- Record Interaction / AI Assist ----------

export type LccInteractionType =
  | 'call'
  | 'text'
  | 'email'
  | 'in_person'
  | 'new_info'
  | 'internal_note';

export type LccInteractionOutcome =
  | 'no_change'
  | 'verified'
  | 'waiting_on_lead'
  | 'callback_needed'
  | 'appointment_set'
  | 'estimate_sent'
  | 'converted'
  | 'lost'
  | 'unverified';

export type LccVerificationMethod =
  | 'call'
  | 'email'
  | 'text'
  | 'in_person'
  | 'ai_receptionist'
  | 'other';

/** Body for POST /api/lcc/leads/:id/interactions. */
export interface LccInteractionInput {
  interactionType: LccInteractionType;
  notes?: string;
  outcome?: LccInteractionOutcome;
  /** Required when outcome is "verified". */
  verificationMethod?: LccVerificationMethod;
  /** What should happen next when this lead returns to LeadLoop. */
  nextStep?: string;
  /** ISO date-time for the return, null to clear, undefined = leave alone. */
  nextFollowUpAt?: string | null;
}

export interface LccInteractionResult {
  lead: LccLead;
  event?: LccLeadEvent;
}

export type LccAssistAction =
  | 'follow_up_email'
  | 'request_info'
  | 'summarize'
  | 'suggest_next_step'
  | 'check_duplicate';

export interface LccAssistResult {
  kind: 'approval' | 'summary' | 'event';
  approval?: LccApproval;
  lead?: LccLead;
  event?: LccLeadEvent;
}

interface ListResponse<T> {
  items: T[];
  total: number;
}

export const lccApi = {
  summary: () => request<LccSummary>('/api/lcc/summary'),
  reports: () => request<LccReports>('/api/lcc/reports'),

  leadEvents: (leadId: string) =>
    request<ListResponse<LccLeadEvent>>(`/api/lcc/leads/${encodeURIComponent(leadId)}/events`),
  addLeadNote: (leadId: string, input: { title: string; description?: string }) =>
    request<LccLeadEvent>(`/api/lcc/leads/${encodeURIComponent(leadId)}/events`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),

  approvals: (status?: 'pending' | 'approved' | 'rejected' | 'all') =>
    request<ListResponse<LccApproval>>(`/api/lcc/approvals${status ? `?status=${status}` : ''}`),
  decideApproval: (id: string, decision: 'approve' | 'reject') =>
    request<LccApproval>(`/api/lcc/approvals/${encodeURIComponent(id)}/${decision}`, {
      method: 'POST',
    }),
  updateApproval: (
    id: string,
    patch: { title?: string; description?: string; draft?: LccApprovalDraft },
  ) =>
    request<LccApproval>(`/api/lcc/approvals/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  regenerateApproval: (id: string, feedback: string) =>
    request<LccApproval>(`/api/lcc/approvals/${encodeURIComponent(id)}/regenerate`, {
      method: 'POST',
      body: JSON.stringify({ feedback }),
    }),
  cancelApproval: (id: string) =>
    request<LccApproval>(`/api/lcc/approvals/${encodeURIComponent(id)}/cancel`, {
      method: 'POST',
    }),

  verifications: () => request<ListResponse<LccVerification>>('/api/lcc/verifications'),
  verifyLead: (leadId: string) =>
    request<LccVerification>(`/api/lcc/leads/${encodeURIComponent(leadId)}/verify`, {
      method: 'POST',
    }),

  // ---------- core lead workflow (this pass) ----------

  dashboard: () => request<LccDashboard>('/api/lcc/dashboard'),
  loop: (filter: LccLoopFilter = 'all') =>
    request<ListResponse<LccLoopItem>>(`/api/lcc/loop?filter=${filter}`),
  /** Full leads list. status: 'all' | 'open' | any snake_case LCC status. */
  leads: (opts: { status?: string; q?: string } = {}) => {
    const params = new URLSearchParams();
    if (opts.status) params.set('status', opts.status);
    if (opts.q) params.set('q', opts.q);
    const qs = params.toString();
    return request<ListResponse<LccLead>>(`/api/lcc/leads${qs ? `?${qs}` : ''}`);
  },
  leadDetail: (leadId: string) =>
    request<LccLeadDetail>(`/api/lcc/leads/${encodeURIComponent(leadId)}/detail`),
  updateLead: (leadId: string, patch: LccLeadPatch) =>
    request<LccLead>(`/api/lcc/leads/${encodeURIComponent(leadId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),
  createLead: (input: LccLeadPatch & { title: string }) =>
    request<LccLead>('/api/lcc/leads', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  recordInteraction: (leadId: string, input: LccInteractionInput) =>
    request<LccInteractionResult>(`/api/lcc/leads/${encodeURIComponent(leadId)}/interactions`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  assist: (leadId: string, action: LccAssistAction) =>
    request<LccAssistResult>(`/api/lcc/leads/${encodeURIComponent(leadId)}/assist`, {
      method: 'POST',
      body: JSON.stringify({ action }),
    }),
  logEmailAction: (leadId: string, input: { subject?: string; note?: string } = {}) =>
    request<{ ok: boolean; eventId: string }>(
      `/api/lcc/leads/${encodeURIComponent(leadId)}/log-email`,
      { method: 'POST', body: JSON.stringify(input) },
    ),
  logCallAction: (leadId: string, input: { outcome?: string; note?: string } = {}) =>
    request<{ ok: boolean; eventId: string }>(
      `/api/lcc/leads/${encodeURIComponent(leadId)}/log-call`,
      { method: 'POST', body: JSON.stringify(input) },
    ),

  organizations: (q?: string) =>
    request<ListResponse<LccOrganizationListItem>>(
      `/api/lcc/organizations${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  organizationDetail: (orgId: string) =>
    request<LccOrganizationDetail>(`/api/lcc/organizations/${encodeURIComponent(orgId)}`),
  updateOrganization: (
    orgId: string,
    patch: Partial<Pick<LccOrganization, 'name' | 'status' | 'website' | 'industry' | 'phone' | 'email' | 'notes'>>,
  ) =>
    request<LccOrganization>(`/api/lcc/organizations/${encodeURIComponent(orgId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  contacts: (q?: string) =>
    request<ListResponse<LccContactListItem>>(
      `/api/lcc/contacts${q ? `?q=${encodeURIComponent(q)}` : ''}`,
    ),
  contactDetail: (contactId: string) =>
    request<LccContactDetail>(`/api/lcc/contacts/${encodeURIComponent(contactId)}`),
  updateContact: (
    contactId: string,
    patch: {
      firstName?: string;
      lastName?: string;
      fullName?: string;
      email?: string;
      phone?: string;
      roleTitle?: string;
      organizationId?: string | null;
    },
  ) =>
    request<LccContact>(`/api/lcc/contacts/${encodeURIComponent(contactId)}`, {
      method: 'PATCH',
      body: JSON.stringify(patch),
    }),

  formSubmissions: () => request<ListResponse<LccFormSubmission>>('/api/lcc/form-submissions'),
};
